import React, { useCallback, useState, useEffect } from "react";
import { Form, Modal } from "@components";
import { Box, Button } from "@material-ui/core";
import { Manager } from "@components/form/state/types";
import { InfrastructureModel } from "@api/types/infrastructure";
import api from "@api";
import { Field } from "../../../../../../components/form/fields/field";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import { useApproverDistroSelect, useBusinessTypeSelect } from "@hooks";
import { useInfraStore } from "../../../../../../store/hooksy";
import { alertService } from "@services";

interface Props {
  open: boolean;
  onCancel: () => void;
  form: Manager<InfrastructureModel>;
}

type Key = keyof InfrastructureModel;

export const InfrastructureDetail: React.FC<Props> = ({
  open,
  onCancel,
  form,
}) => {
  const [, setInfraStore] = useInfraStore();

  // custom hooks
  const {
    approverDistros,
    isFetchingApproverDistros,
    selectedApproverDistro2,
    selectedApproverDistro3,
    selectedApproverDistro4,
    setSelectedApproverDistro2,
    setSelectedApproverDistro3,
    setSelectedApproverDistro4,
    handleApproverDistroTypeChange,
  } = useApproverDistroSelect(
    null,
    form.values.approverDistro2,
    form.values.approverDistro3,
    form.values.approverDistro4
  );

  // custom hooks
  const {
    selectedBusinessType,
    selectedBusinessTypeName,
    businessTypes,
    isFetchingbusinessTypes,
    setSelectedBusinessType,
    setSelectedBusinessTypeName,
    handleBusinessTypeChange,
  } = useBusinessTypeSelect(0, form.values.systemType);

  let infraModel: InfrastructureModel = {
    // infrastructure name
    id: 0,
    typeName: "",
    isActive: false,
    isWithAppName: false,
    approver1: "",
    approver2: "",
    approver3: "",
    approver4: "",
    approverDistro2: null,
    approverDistro3: null,
    approverDistro4: null,
    // business unit
    systemType: "",
    metaData: "",
    uamNote: "",
    assignmentTeam: "",
    accessDetailNote: "",
  };

  const [isSubmitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.approver
      .getApproverDistro()
      .then((response) => {
        console.log(response);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    if (selectedApproverDistro2) {
      form.set(Form.target.VALUE, ["approver2", ""]);
    }
    if (selectedApproverDistro3) {
      form.set(Form.target.VALUE, ["approver3", ""]);
    }
    if (selectedApproverDistro4) {
      form.set(Form.target.VALUE, ["approver4", ""]);
    }
  }, [
    selectedApproverDistro2,
    selectedApproverDistro3,
    selectedApproverDistro4,
  ]);

  const [businessTypeName, setBusinessTypeName] = useState("");
  useEffect(() => {
    if (selectedBusinessType) {
      const businessTypeFilteredById: any = businessTypes.find(
        (x: any) => x.value === selectedBusinessType
      );
      setBusinessTypeName(businessTypeFilteredById?.label ?? "");
    } else {
      if (form.values.systemType) {
        setBusinessTypeName(form.values.systemType);
      } else {
        setBusinessTypeName("");
      }
    }
  }, [selectedBusinessType, businessTypes, form]);

  const handleSubmit = useCallback(() => {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          submitData();
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }, [
    form,
    selectedBusinessTypeName,
    selectedApproverDistro2,
    selectedApproverDistro3,
    selectedApproverDistro4,
    businessTypeName,
  ]);

  function submitData() {
    infraModel.id = form.values.id;
    infraModel.typeName = form.values.typeName;
    infraModel.isActive = form.values.isActive;
    infraModel.isWithAppName = form.values.isWithAppName;
    infraModel.approver2 = form.values.approver2;
    infraModel.approver3 = form.values.approver3;
    infraModel.approver4 = form.values.approver4;
    infraModel.approverDistro2 =
      selectedApproverDistro2 != 0 ? selectedApproverDistro2 : null;
    infraModel.approverDistro3 =
      selectedApproverDistro3 != 0 ? selectedApproverDistro3 : null;
    infraModel.approverDistro4 =
      selectedApproverDistro4 != 0 ? selectedApproverDistro4 : null;
    infraModel.systemType = businessTypeName;
    infraModel.metaData = form.values.metaData;
    infraModel.uamNote = form.values.uamNote;
    infraModel.assignmentTeam = form.values.assignmentTeam;
    infraModel.accessDetailNote = form.values.accessDetailNote;
    setSubmitting(true);
    if (infraModel.id !== 0) {
      // UPDATE
      api.infrastructure
        .putInfra(infraModel.id, infraModel)
        .then((response) => {
          alertService.success({
            msg: "Infrastructure has been successfully updated.",
          });
          customCloseModal();
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({ statusCode: error.response.status });
          }
        })
        .then(() => {
          setSubmitting(false);
          setInfraStore({ fetchList: true });
        });
    } else {
      // CREATE
      api.infrastructure
        .postInfra(infraModel)
        .then((response) => {
          alertService.success({
            msg: "Infrastructure has been successfully created.",
          });
          customCloseModal();
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({ statusCode: error.response.status });
          }
        })
        .then(() => {
          setSubmitting(false);
          setInfraStore({ fetchList: true });
        });
    }
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "typeName":
        return Promise.resolve([name as Key, Form.Validators.hasValue(value)]);
      case "approver2":
        if (form.values.approver2) {
          return Promise.resolve([
            name as Key,
            Form.Validators.isEmail(value as string),
          ]);
        }
      case "approver3":
        if (form.values.approver3) {
          return Promise.resolve([
            name as Key,
            Form.Validators.isEmail(value as string),
          ]);
        }
      case "approver4":
        if (form.values.approver4) {
          return Promise.resolve([
            name as Key,
            Form.Validators.isEmail(value as string),
          ]);
        }
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function onChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
    if (name === "approver2") setSelectedApproverDistro2(0);
    if (name === "approver3") setSelectedApproverDistro3(0);
    if (name === "approver4") setSelectedApproverDistro4(0);
  }

  function clearInfrastructureModel() {
    // infrastructure name
    infraModel.id = 0;
    infraModel.typeName = "";
    infraModel.isActive = false;
    infraModel.isWithAppName = false;
    infraModel.approver1 = "";
    infraModel.approver2 = "";
    infraModel.approver3 = "";
    infraModel.approver4 = "";
    infraModel.approverDistro2 = null;
    infraModel.approverDistro3 = null;
    infraModel.approverDistro4 = null;
    // business unit
    infraModel.systemType = "";
    infraModel.metaData = "";
    infraModel.uamNote = "";
    infraModel.assignmentTeam = "";
    infraModel.accessDetailNote = "";
    onCancel();
  }

  function customCloseModal() {
    clearInfrastructureModel();
    onCancel();
    form.reset();
  }

  const modalTitle =
    form.values.id !== 0 ? "Update Infrastructure" : "Add New Infrastructure";
  const submitText = isSubmitting ? "Submitting..." : "Submit";

  let approverDistroMenu = null;
  if (isFetchingApproverDistros) {
    approverDistroMenu = <MenuItem value={0}>Please wait...</MenuItem>;
  } else {
    approverDistroMenu = approverDistros.map((item: any, key) => (
      <MenuItem key={key} value={item.value}>
        {item.label}
      </MenuItem>
    ));
  }

  let businessTypeMenu = null;
  if (isFetchingbusinessTypes) {
    businessTypeMenu = <MenuItem value={0}>Please wait...</MenuItem>;
  } else {
    businessTypeMenu = businessTypes.map((item: any, key) => (
      <MenuItem key={key} value={item.value}>
        {item.label}
      </MenuItem>
    ));
  }

  return (
    <Modal
      isOpen={open}
      title={modalTitle}
      closeModal={() => customCloseModal()}
      size="sm"
    >
      <Form>
        <Form.Textbox
          label="Infrastructure Name"
          name="typeName"
          onChange={onChange}
          size={12}
          error={form.errors.typeName}
          value={form.values.typeName}
        />
        <Form.Checkbox
          label="Is Active"
          name="isActive"
          onChange={onChange}
          value={form.values.isActive}
          size={6}
        />
        <Form.Checkbox
          label="With App Name"
          name="isWithAppName"
          onChange={onChange}
          value={form.values.isWithAppName}
          size={6}
        />
        <Form.Textbox
          label="Approver 2"
          name="approver2"
          onChange={onChange}
          size={12}
          error={form.errors.approver2}
          value={form.values.approver2}
        />
        <Form.Textbox
          label="Approver 3"
          name="approver3"
          onChange={onChange}
          size={12}
          error={form.errors.approver3}
          value={form.values.approver3}
        />
        <Form.Textbox
          label="Approver 4"
          name="approver4"
          onChange={onChange}
          size={12}
          error={form.errors.approver4}
          value={form.values.approver4}
        />
        <Field label="Approver Distro 2" size={12}>
          <Select
            name="approverDistro2"
            value={selectedApproverDistro2}
            onChange={handleApproverDistroTypeChange}
            disabled={isFetchingApproverDistros}
          >
            {approverDistroMenu}
          </Select>
        </Field>
        <Field label="Approver Distro 3" size={12}>
          <Select
            name="approverDistro3"
            value={selectedApproverDistro3}
            onChange={handleApproverDistroTypeChange}
            disabled={isFetchingApproverDistros}
          >
            {approverDistroMenu}
          </Select>
        </Field>
        <Field label="Approver Distro 4" size={12}>
          <Select
            name="approverDistro4"
            value={selectedApproverDistro4}
            onChange={handleApproverDistroTypeChange}
            disabled={isFetchingApproverDistros}
          >
            {approverDistroMenu}
          </Select>
        </Field>
        <Field label="Business Unit" size={12}>
          <Select
            name="businessUnit"
            value={selectedBusinessType}
            onChange={handleBusinessTypeChange}
            disabled={isFetchingbusinessTypes}
          >
            {businessTypeMenu}
          </Select>
        </Field>
        <Form.Textbox
          label="Meta Data"
          name="metaData"
          onChange={onChange}
          size={12}
          error={form.errors.metaData}
          value={form.values.metaData}
        />
        <Form.Textbox
          label="UAM Note"
          name="uamNote"
          onChange={onChange}
          size={12}
          error={form.errors.uamNote}
          value={form.values.uamNote}
        />
        <Form.Textbox
          label="Access Detail Note"
          name="accessDetailNote"
          onChange={onChange}
          size={12}
          error={form.errors.accessDetailNote}
          value={form.values.accessDetailNote}
        />
        <Form.Textbox
          label="Service Manager Queue"
          name="assignmentTeam"
          onChange={onChange}
          size={6}
          error={form.errors.assignmentTeam}
          value={form.values.assignmentTeam}
        />
      </Form>
      <Box textAlign="right" mt={4}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => customCloseModal()}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="button"
          onClick={handleSubmit}
          style={{ marginLeft: "40px" }}
          disabled={isSubmitting}
        >
          {submitText}
        </Button>
      </Box>
    </Modal>
  );
};
